import React, { useCallback } from 'react';
import { Input, Tabs, Space, Select, Button, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useActiveLocales, TranslationsFieldLocale } from '../hooks/useActiveLocales';

export type { TranslationsFieldLocale };

interface TranslationsFieldProps {
  /** Current value from Form.Item */
  value?: Record<string, Record<string, string | string[]>>;
  /** onChange callback for Form.Item */
  onChange?: (value: Record<string, Record<string, string | string[]>>) => void;
  /** List of available locales to add. When omitted, active locales are fetched from the API. */
  availableLocales?: TranslationsFieldLocale[];
  /** Which fields to render per locale. Defaults to name + description. */
  fields?: { key: string; label: string; placeholder?: string; textarea?: boolean; array?: boolean }[];
}

type FieldConfig = { key: string; label: string; placeholder?: string; textarea?: boolean; array?: boolean };

const FALLBACK_LOCALES: TranslationsFieldLocale[] = [
  { code: 'zh-HK', label: 'ZH-HK' },
  { code: 'zh-CN', label: 'ZH-CN' },
];

const DEFAULT_FIELDS: { key: string; label: string; placeholder?: string; textarea?: boolean; array?: boolean }[] = [
  { key: 'name', label: 'Name', placeholder: 'Translated name' },
  { key: 'description', label: 'Description', placeholder: 'Translated description', textarea: true },
];

/**
 * Reusable translations editor compatible with Ant Design Form.Item.
 * Renders tabs per locale with configurable fields.
 *
 * Usage:
 *   <Form.Item label="Translations" name="translations">
 *     <TranslationsField />
 *   </Form.Item>
 */
export const TranslationsField: React.FC<TranslationsFieldProps> = ({
  value,
  onChange,
  availableLocales: availableLocalesProp,
  fields = DEFAULT_FIELDS,
}) => {
  const { locales: apiLocales, loading } = useActiveLocales();

  const translations = value || {};
  const activeLocales = Object.keys(translations);

  const updateField = useCallback(
    (locale: string, fieldKey: string, fieldValue: string | string[]) => {
      const next = {
        ...translations,
        [locale]: { ...(translations[locale] || {}), [fieldKey]: fieldValue },
      };
      onChange?.(next);
    },
    [translations, onChange],
  );

  const addLocale = useCallback(
    (code: string) => {
      if (!translations[code]) {
        const next = { ...translations, [code]: {} };
        onChange?.(next);
      }
    },
    [translations, onChange],
  );

  const removeLocale = useCallback(
    (code: string) => {
      const next = { ...translations };
      delete next[code];
      onChange?.(next);
    },
    [translations, onChange],
  );

  // Use explicitly passed locales, then API locales, then fallback
  let resolvedLocales: TranslationsFieldLocale[];
  if (availableLocalesProp) {
    resolvedLocales = availableLocalesProp;
  } else if (apiLocales.length > 0) {
    resolvedLocales = apiLocales;
  } else {
    resolvedLocales = FALLBACK_LOCALES;
  }

  if (loading) {
    return <Spin size="small" style={{ display: 'block', margin: '12px auto' }} />;
  }

  const unusedLocales = resolvedLocales.filter(
    (l) => !activeLocales.includes(l.code),
  );

  const localeLabels: Record<string, string> = {};
  for (const l of resolvedLocales) localeLabels[l.code] = l.label;

  const tabItems = activeLocales.map((locale) => ({
    key: locale,
    label: (
      <span>
        {localeLabels[locale] || locale}
        <DeleteOutlined
          style={{ marginLeft: 6, fontSize: 11, color: '#ff4d4f' }}
          onClick={(e) => { e.stopPropagation(); removeLocale(locale); }}
        />
      </span>
    ),
    children: (
      <div style={{ paddingTop: 8 }}>
        {fields.map((f: FieldConfig) => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4, color: '#595959' }}>
              {f.label}
            </label>
            {f.array ? (
              <div>
                {((translations[locale]?.[f.key] as string[]) || []).map((item, idx, arr) => (
                  <div key={idx} style={{ display: 'flex', marginBottom: 4, gap: 4 }}>
                    <Input
                      placeholder={f.placeholder}
                      value={item}
                      onChange={(e) => {
                        const next = [...arr];
                        next[idx] = e.target.value;
                        updateField(locale, f.key, next);
                      }}
                      style={{ flex: 1 }}
                    />
                    <Button
                      icon={<MinusCircleOutlined />}
                      onClick={() => {
                        const next = [...arr];
                        next.splice(idx, 1);
                        updateField(locale, f.key, next);
                      }}
                      type="text"
                      danger
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => {
                    const arr = (translations[locale]?.[f.key] as string[]) || [];
                    updateField(locale, f.key, [...arr, '']);
                  }}
                  icon={<PlusOutlined />}
                  size="small"
                >
                  Add
                </Button>
              </div>
            ) : f.textarea ? (
              <Input.TextArea
                rows={2}
                placeholder={f.placeholder}
                value={(translations[locale]?.[f.key] as string) || ''}
                onChange={(e) => updateField(locale, f.key, e.target.value)}
              />
            ) : (
              <Input
                placeholder={f.placeholder}
                value={(translations[locale]?.[f.key] as string) || ''}
                onChange={(e) => updateField(locale, f.key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>
    ),
  }));

  return (
    <div>
      {tabItems.length > 0 && <Tabs items={tabItems} size="small" />}
      {unusedLocales.length > 0 && (
        <Space style={{ marginTop: tabItems.length > 0 ? -8 : 0 }}>
          <span style={{ fontSize: 13, color: '#898989' }}>Add locale:</span>
          <Select
            placeholder="Select locale"
            style={{ width: 140 }}
            onChange={addLocale}
            value={undefined}
            options={unusedLocales.map((l) => ({ value: l.code, label: l.label }))}
          />
        </Space>
      )}
    </div>
  );
};
